import React from "react";
import DocsPageHeader from "../../../components/dashboard/common/Documentation/DocsPageHeader";
import SearchHighlighter from "../../../components/dashboard/common/Documentation/SearchHighlighter";
import DocsCallout from "../../../components/dashboard/common/Documentation/DocsCallout";

const EndpointDiscovery = () => {
  return (
    <SearchHighlighter>
      <DocsPageHeader 
        number="3" 
        title="Endpoint Provisioning & Discovery" 
        description="Endpoint Discovery catalogs every technical resource within your targets — individual paths, ports, parameters, and files — giving researchers a precise, auditable map of your attack surface."
      />

      <h2 id="resource-types" className="text-xl font-bold text-black mt-10 mb-4">3.1 Endpoint Resource Specification</h2>
      <p className="mb-6 text-slate-600">Provisioning specific endpoints allows the platform to intelligently route tests. The available resource types are filtered based on the <b>Target Type</b> to ensure technical relevancy.</p>

      <div className="overflow-x-auto border border-[#eaeaea] rounded-xl mb-10">
        <table className="min-w-full divide-y divide-[#eaeaea] m-0">
          <thead className="bg-[#fafafa]">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-bold text-black uppercase tracking-wider border-r border-[#eaeaea]">Target Category</th>
              <th className="px-6 py-3 text-left text-xs font-bold text-black uppercase tracking-wider">Available Resource Types</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-[#eaeaea]">
            <tr>
              <td className="px-6 py-4 text-sm font-bold text-black border-r border-[#eaeaea]">Web Application / API / Webhook</td>
              <td className="px-6 py-4 text-sm text-slate-600">
                <div className="flex flex-wrap gap-2">
                  <span className="px-2 py-0.5 bg-blue-50 text-blue-700 border border-blue-100 rounded text-[10px] font-bold">Path</span>
                  <span className="px-2 py-0.5 bg-blue-50 text-blue-700 border border-blue-100 rounded text-[10px] font-bold">Parameter</span>
                  <span className="px-2 py-0.5 bg-blue-50 text-blue-700 border border-blue-100 rounded text-[10px] font-bold">Header</span>
                  <span className="px-2 py-0.5 bg-blue-50 text-blue-700 border border-blue-100 rounded text-[10px] font-bold">File</span>
                </div>
              </td>
            </tr>
            <tr>
              <td className="px-6 py-4 text-sm font-bold text-black border-r border-[#eaeaea]">External IP / Hostname</td>
              <td className="px-6 py-4 text-sm text-slate-600">
                <div className="flex flex-wrap gap-2">
                  <span className="px-2 py-0.5 bg-slate-50 text-slate-700 border border-slate-200 rounded text-[10px] font-bold">Port</span>
                  <span className="px-2 py-0.5 bg-blue-50 text-blue-700 border border-blue-100 rounded text-[10px] font-bold">Path</span>
                  <span className="px-2 py-0.5 bg-slate-50 text-slate-700 border border-slate-200 rounded text-[10px] font-bold">IP</span>
                </div>
              </td>
            </tr>
            <tr>
              <td className="px-6 py-4 text-sm font-bold text-black border-r border-[#eaeaea]">CIDR / Network Segment</td>
              <td className="px-6 py-4 text-sm text-slate-600">
                <div className="flex flex-wrap gap-2">
                  <span className="px-2 py-0.5 bg-slate-50 text-slate-700 border border-slate-200 rounded text-[10px] font-bold">IP</span>
                  <span className="px-2 py-0.5 bg-slate-50 text-slate-700 border border-slate-200 rounded text-[10px] font-bold">Port</span>
                </div>
              </td>
            </tr>
            <tr>
              <td className="px-6 py-4 text-sm font-bold text-black border-r border-[#eaeaea]">Mobile Application</td>
              <td className="px-6 py-4 text-sm text-slate-600">
                <div className="flex flex-wrap gap-2">
                  <span className="px-2 py-0.5 bg-blue-50 text-blue-700 border border-blue-100 rounded text-[10px] font-bold">Path</span>
                  <span className="px-2 py-0.5 bg-blue-50 text-blue-700 border border-blue-100 rounded text-[10px] font-bold">Parameter</span>
                  <span className="px-2 py-0.5 bg-blue-50 text-blue-700 border border-blue-100 rounded text-[10px] font-bold">Header</span>
                  <span className="px-2 py-0.5 bg-blue-50 text-blue-700 border border-blue-100 rounded text-[10px] font-bold">File (Binary / Assets)</span>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <h2 id="field-specs" className="text-xl font-bold text-black mt-12 mb-4">3.2 Provisioning Field Specifications</h2>
      <p className="text-slate-600 mb-6 leading-relaxed">The following fields are required when adding a single resource or performing a bulk import.</p>
      
      <div className="space-y-6">
        <div className="border-l-4 border-blue-500 pl-6 py-1">
          <h4 className="font-bold text-black">Resource Value</h4>
          <p className="text-sm text-slate-500 mt-1">
            The technical identifier. Validated based on type:
          </p>
          <ul className="text-xs text-slate-400 mt-2 space-y-1">
            <li><b>IP:</b> Standard IPv4 or IPv6 format.</li>
            <li><b>Path:</b> URI strings (e.g., <code>/api/v1/auth</code>).</li>
            <li><b>Port:</b> Numerical values between 1 and 65535.</li>
          </ul>
        </div>

        <div className="border-l-4 border-orange-500 pl-6 py-1">
          <h4 className="font-bold text-black">HTTP Method</h4>
          <p className="text-sm text-slate-500 mt-1">
            Required <b>only for Path</b> resources. Options include <code>GET, POST, PUT, DELETE, PATCH, HEAD, OPTIONS</code>. Auto-assigned to <code>GET</code> for static web pages and files.
          </p>
        </div>

        <div className="border-l-4 border-slate-300 pl-6 py-1">
          <h4 className="font-bold text-black">Initial Status</h4>
          <p className="text-sm text-slate-500 mt-1">
            Control the starting state of the endpoint. <b>Admin/Business</b> users can set this to <code>In Scope</code>, <code>Out of Scope</code>, or <code>Excluded</code> during creation.
          </p>
        </div>

        <div className="border-l-4 border-slate-300 pl-6 py-1">
          <h4 className="font-bold text-black">Program Linking</h4>
          <p className="text-sm text-slate-500 mt-1">
            <b>Researchers only:</b> Every discovered endpoint must be linked to an active, in-progress Program ID for context-aware security reporting.
          </p>
        </div>

        <div className="border-l-4 border-slate-300 pl-6 py-1">
          <h4 className="font-bold text-black">Scope Notes (Recommended)</h4>
          <p className="text-sm text-slate-500 mt-1">
            Technical or business context for the resource. Required for all researcher discoveries to detail the exact discovery vector.
          </p>
        </div>
      </div>

      <h2 id="provisioning-modes" className="text-xl font-bold text-black mt-12 mb-4">3.3 Provisioning Modes</h2>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-6">
        <div className="rounded-2xl border border-[#eaeaea] p-5 bg-[#fafafa]/50">
          <h3 className="text-sm font-bold text-black mb-2 uppercase tracking-wide">Single Provision</h3>
          <p className="text-xs text-slate-500 leading-relaxed">
            Specify the resource type, value, and HTTP method. Ideal for documenting critical entry points or restricted admin routes.
          </p>
        </div>
        <div className="rounded-2xl border border-[#eaeaea] p-5 bg-[#fafafa]/50">
          <h3 className="text-sm font-bold text-black mb-2 uppercase tracking-wide">Bulk Pasting</h3>
          <p className="text-xs text-slate-500 leading-relaxed">
            Paste a list of path-only endpoints. The platform will automatically tag them with the selected resource type (e.g., API Endpoint).
          </p>
        </div>
        <div className="rounded-2xl border border-[#eaeaea] p-5 bg-[#fafafa]/50">
          <h3 className="text-sm font-bold text-black mb-2 uppercase tracking-wide">File Import</h3>
          <p className="text-xs text-slate-500 leading-relaxed">
            Upload CSV or JSON exports from tools like Burp Suite or Postman. Supports normalization of method names and path formats.
          </p>
        </div>
      </div>

      <h2 id="normalization" className="text-xl font-bold text-black mt-12 mb-4">3.4 Technical Specification & Normalization</h2>
      <p className="text-slate-600 mb-6 leading-relaxed">
        The platform uses a strict technical specification for imported endpoints. Use the following mappings when preparing CSV or JSON files for bulk import.
      </p>

      <div className="overflow-x-auto border border-[#eaeaea] rounded-xl mb-10">
        <table className="min-w-full divide-y divide-[#eaeaea] m-0">
          <thead className="bg-[#fafafa]">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-bold text-black uppercase tracking-wider border-r border-[#eaeaea]">`resource_type` (in-file)</th>
              <th className="px-6 py-3 text-left text-xs font-bold text-black uppercase tracking-wider border-r border-[#eaeaea]">Description / Purpose</th>
              <th className="px-6 py-3 text-left text-xs font-bold text-black uppercase tracking-wider">Sample Value</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-[#eaeaea]">
            <tr><td className="px-6 py-4 text-sm font-bold text-black border-r border-[#eaeaea]">PATH</td><td className="px-6 py-4 text-sm text-slate-600 border-r border-[#eaeaea]">Standard Web Pages or API Routes</td><td className="px-6 py-4 text-sm font-mono text-slate-500">/api/v1/auth</td></tr>
            <tr><td className="px-6 py-4 text-sm font-bold text-black border-r border-[#eaeaea]">IP</td><td className="px-6 py-4 text-sm text-slate-600 border-r border-[#eaeaea]">Server IP addresses (IPv4)</td><td className="px-6 py-4 text-sm font-mono text-slate-500">192.168.1.1</td></tr>
            <tr><td className="px-6 py-4 text-sm font-bold text-black border-r border-[#eaeaea]">PORT</td><td className="px-6 py-4 text-sm text-slate-600 border-r border-[#eaeaea]">Network service ports</td><td className="px-6 py-4 text-sm font-mono text-slate-500">443, 8443, 80</td></tr>
            <tr><td className="px-6 py-4 text-sm font-bold text-black border-r border-[#eaeaea]">PARAMETER</td><td className="px-6 py-4 text-sm text-slate-600 border-r border-[#eaeaea]">URL or Request body parameters</td><td className="px-6 py-4 text-sm font-mono text-slate-500">user_id, token</td></tr>
            <tr><td className="px-6 py-4 text-sm font-bold text-black border-r border-[#eaeaea]">HEADER</td><td className="px-6 py-4 text-sm text-slate-600 border-r border-[#eaeaea]">HTTP Request headers</td><td className="px-6 py-4 text-sm font-mono text-slate-500">Authorization</td></tr>
            <tr><td className="px-6 py-4 text-sm font-bold text-black border-r border-[#eaeaea]">FILE</td><td className="px-6 py-4 text-sm text-slate-600 border-r border-[#eaeaea]">Shared file paths or assets</td><td className="px-6 py-4 text-sm font-mono text-slate-500">/report.pdf</td></tr>
            <tr><td className="px-6 py-4 text-sm font-bold text-black border-r border-[#eaeaea]">OTHER</td><td className="px-6 py-4 text-sm text-slate-600 border-r border-[#eaeaea]">Custom resource types</td><td className="px-6 py-4 text-sm font-mono text-slate-500">N/A</td></tr>
          </tbody>
        </table>
      </div>

      <div className="rounded-2xl border border-[#eaeaea] overflow-hidden mb-10">
        <div className="px-6 py-4 bg-[#fafafa] border-b border-[#eaeaea]">
          <h3 className="text-sm font-bold text-black uppercase tracking-widest">`http_method` Applicability</h3>
        </div>
        <div className="p-6 bg-white space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="flex items-center gap-3">
              <span className="px-2 py-0.5 bg-blue-50 text-blue-600 border border-blue-100 rounded text-[10px] font-black">GET</span>
              <span className="text-xs text-slate-500">Fetch data or View page</span>
            </div>
            <div className="flex items-center gap-3">
              <span className="px-2 py-0.5 bg-green-50 text-green-600 border border-green-100 rounded text-[10px] font-black">POST</span>
              <span className="text-xs text-slate-500">Submit data or Login</span>
            </div>
            <div className="flex items-center gap-3">
              <span className="px-2 py-0.5 bg-orange-50 text-orange-600 border border-orange-100 rounded text-[10px] font-black">PUT / PATCH</span>
              <span className="text-xs text-slate-500">Update existing resource</span>
            </div>
            <div className="flex items-center gap-3">
              <span className="px-2 py-0.5 bg-red-50 text-red-600 border border-red-100 rounded text-[10px] font-black">DELETE</span>
              <span className="text-xs text-slate-500">Remove resource</span>
            </div>
          </div>
        </div>
      </div>

      <div className="rounded-2xl border border-[#eaeaea] overflow-hidden">
        <div className="px-6 py-4 bg-[#fafafa] border-b border-[#eaeaea]">
          <h3 className="text-sm font-bold text-black uppercase tracking-widest">Import Normalization Rules</h3>
        </div>
        <div className="p-6 bg-white space-y-4">
          <div className="flex items-start gap-4">
            <div className="w-1.5 h-1.5 rounded-full bg-[#0070f3] mt-2 shrink-0" />
            <p className="text-sm text-slate-600"><b className="text-black">Case-Insensitivity:</b> Values like <code>get</code>, <code>path</code>, or <code>ip</code> are automatically normalized to standard uppercase.</p>
          </div>
          <div className="flex items-start gap-4">
            <div className="w-1.5 h-1.5 rounded-full bg-[#0070f3] mt-2 shrink-0" />
            <p className="text-sm text-slate-600"><b className="text-black">Duplicate Suppression:</b> Same <code>value</code> + <code>resource_type</code> pairs within the same target are collapsed into a single record.</p>
          </div>
          <div className="flex items-start gap-4">
            <div className="w-1.5 h-1.5 rounded-full bg-[#0070f3] mt-2 shrink-0" />
            <p className="text-sm text-slate-600"><b className="text-black">Syntax Guards:</b> IP addresses must be valid IPv4, and Port numbers must be in the range <code>[1-65535]</code>.</p>
          </div>
        </div>
      </div>

      <h2 id="researcher-discovery" className="text-xl font-bold text-black mt-12 mb-4">3.5 Researcher-Led Continuous Discovery</h2>
      <p className="text-slate-600 leading-relaxed mb-6">
        Discovery is a continuous process. PTaaS researchers monitor your targets during active programs to surface shadow resources.
      </p>
      
      <DocsCallout type="note">
        <b>Traceable Discovery:</b> Every researcher-discovered endpoint includes <b>Scope Notes</b> detailing the discovery vector (e.g., "Identified in main.js source analysis"). These are submitted for your approval before inclusion in the canonical inventory.
      </DocsCallout>

    </SearchHighlighter>
  );
};

export default EndpointDiscovery;
